/**
 * Centralized subsidiaryKey configuration for all apps
 *
 * The subsidiaryKey is the Umbraco GUID from /he-il/shared/general-information/groups-websites/
 * that identifies each website's root node in the CMS.
 *
 * This configuration provides environment-specific GUIDs for all apps in the monorepo.
 *
 * @example
 * ```tsx
 * import { getSubsidiaryKey } from "utils"
 *
 * export const subsidiaryKey = getSubsidiaryKey("ashtrom-corporate")
 * ```
 */

import type { AppName } from "./searchConfig"

export type { AppName }

/**
 * Environment type for subsidiaryKey lookup
 */
export type Environment = "qa" | "preprod" | "production"

/**
 * subsidiaryKey can be either a single GUID string or an array of GUIDs
 * (engineering app uses multiple GUIDs for different divisions)
 */
export type SubsidiaryKey = string | string[]

interface SubsidiaryKeyConfig {
  qa: SubsidiaryKey | null
  preprod: SubsidiaryKey | null
  production: SubsidiaryKey | null
}

/**
 * Complete subsidiaryKey configuration for all apps and environments
 *
 * Generated from scripts/fetch-subsidiary-keys.ts
 * Last updated: 2025-12-18
 */
export const SUBSIDIARY_KEYS: Record<AppName, SubsidiaryKeyConfig> = {
  "ashtrom-corporate": {
    qa: "9e366047-f965-40b7-8a4c-64e4c6a48951",
    preprod: "9e366047-f965-40b7-8a4c-64e4c6a48951",
    production: "9e366047-f965-40b7-8a4c-64e4c6a48951",
  },
  "ashtrom-construction": {
    qa: "b775db85-6543-4fb0-a200-196a3ceab625",
    preprod: "b775db85-6543-4fb0-a200-196a3ceab625",
    production: "b775db85-6543-4fb0-a200-196a3ceab625",
  },
  "ashtrom-4rent": {
    qa: "1cba4690-6d74-4add-9663-8be7ef0fa500",
    preprod: "1cba4690-6d74-4add-9663-8be7ef0fa500",
    production: "1cba4690-6d74-4add-9663-8be7ef0fa500",
  },
  "ashtrom-ashdar": {
    qa: "b58e4b44-5db7-4b00-957b-70d02e7101d2",
    preprod: "b58e4b44-5db7-4b00-957b-70d02e7101d2",
    production: "b58e4b44-5db7-4b00-957b-70d02e7101d2",
  },
  "ashtrom-industries": {
    qa: "f094ee3d-b676-4644-8e9f-90b12c6ff214",
    preprod: "f094ee3d-b676-4644-8e9f-90b12c6ff214",
    production: "f094ee3d-b676-4644-8e9f-90b12c6ff214",
  },
  "ashtrom-energy": {
    qa: "5d512033-5918-41b1-8f83-b567e8810128",
    preprod: "5d512033-5918-41b1-8f83-b567e8810128",
    production: "5d512033-5918-41b1-8f83-b567e8810128",
  },
  "ashtrom-international": {
    qa: "53463851-31e4-4bcf-956a-5a1912f4a108",
    preprod: "53463851-31e4-4bcf-956a-5a1912f4a108",
    production: "53463851-31e4-4bcf-956a-5a1912f4a108",
  },
  "ashtrom-concessions": {
    qa: "93f0a7a0-8191-4b50-8e17-1fff937fee59",
    preprod: "93f0a7a0-8191-4b50-8e17-1fff937fee59",
    production: "93f0a7a0-8191-4b50-8e17-1fff937fee59",
  },
  "ashtrom-base": {
    qa: "b076126b-c5d7-4bec-9355-91cf7e880fc1",
    preprod: "3510f78a-eb4b-45dd-a6ac-182efb03b5f6",
    production: "3510f78a-eb4b-45dd-a6ac-182efb03b5f6",
  },
  "ashtrom-ashcrete": {
    qa: "891483e4-365c-4889-808d-ca12786fcba7",
    preprod: "a711c095-940e-4a77-81e4-56a740c8277d",
    production: "a711c095-940e-4a77-81e4-56a740c8277d",
  },
  "ashtrom-melet": {
    qa: "8fd2a025-68a5-4d10-ad1e-84715f432587",
    preprod: "e201d3f0-dc67-4cf1-8d3d-5209c56a2275",
    production: "e201d3f0-dc67-4cf1-8d3d-5209c56a2275",
  },
  "ashtrom-engineering": {
    qa: [
      "c6910c55-9b17-4d85-a315-52269299dafc",
      "f5200088-1464-4fc6-a0ba-614aaead612c",
      "98865e94-0534-4aa6-bc31-a08ca89d07d0",
      "cdbeaea8-6413-4250-b635-e76ed8363cb0",
    ],
    preprod: [
      "c6910c55-9b17-4d85-a315-52269299dafc",
      "f5200088-1464-4fc6-a0ba-614aaead612c",
      "98865e94-0534-4aa6-bc31-a08ca89d07d0",
      "cdbeaea8-6413-4250-b635-e76ed8363cb0",
    ],
    production: [
      "c6910c55-9b17-4d85-a315-52269299dafc",
      "f5200088-1464-4fc6-a0ba-614aaead612c",
      "98865e94-0534-4aa6-bc31-a08ca89d07d0",
      "cdbeaea8-6413-4250-b635-e76ed8363cb0",
    ],
  },
  "ashtrom-drachim": {
    qa: "a7999c97-364a-4724-aa13-bef7f4723184",
    preprod: "ea472aa9-2f52-4254-8474-cd5c96436f05",
    production: "ea472aa9-2f52-4254-8474-cd5c96436f05",
  },
  "ashtrom-properties": {
    qa: "5b910c96-81b7-49c0-b2a3-f66be295c289",
    preprod: "5b910c96-81b7-49c0-b2a3-f66be295c289",
    production: "5b910c96-81b7-49c0-b2a3-f66be295c289",
  },
  "tierra-bonita": {
    qa: "5d512033-5918-41b1-8f83-b567e8810128",
    preprod: "5d512033-5918-41b1-8f83-b567e8810128",
    production: "5d512033-5918-41b1-8f83-b567e8810128",
  },
  ashkalit: {
    qa: "f6a9af9b-6b89-4062-a70b-f0f84178d6b4",
    preprod: "f6a9af9b-6b89-4062-a70b-f0f84178d6b4",
    production: "f6a9af9b-6b89-4062-a70b-f0f84178d6b4",
  },
  ashbond: {
    qa: "afea80ac-a8bf-4d42-bc6d-2d141146b275",
    preprod: "afea80ac-a8bf-4d42-bc6d-2d141146b275",
    production: "afea80ac-a8bf-4d42-bc6d-2d141146b275",
  },
  israbeton: {
    qa: "9bc9f240-3564-492d-bb43-63041db00111",
    preprod: "9bc9f240-3564-492d-bb43-63041db00111",
    production: "9bc9f240-3564-492d-bb43-63041db00111",
  },
  itumit: {
    qa: "f76b257c-ee65-4193-9063-b06c83383df6",
    preprod: "f76b257c-ee65-4193-9063-b06c83383df6",
    production: "f76b257c-ee65-4193-9063-b06c83383df6",
  },
  quarries: {
    qa: "f0d75174-c668-4b15-8988-b7a10fcfac86",
    preprod: "f0d75174-c668-4b15-8988-b7a10fcfac86",
    production: "f0d75174-c668-4b15-8988-b7a10fcfac86",
  },
  "bg-bond": {
    qa: "1be890a6-6f45-4b55-967e-b40474e7e405",
    preprod: "ab9fe9a3-a79b-4e28-8603-9fafe8cc5eb8",
    production: "ab9fe9a3-a79b-4e28-8603-9fafe8cc5eb8",
  },
  "bg-bond-rebuild": {
    qa: "9e366047-f965-40b7-8a4c-64e4c6a48951",
    preprod: "9e366047-f965-40b7-8a4c-64e4c6a48951",
    production: "9e366047-f965-40b7-8a4c-64e4c6a48951",
  },
  "el-patrimonio": {
    qa: "5d512033-5918-41b1-8f83-b567e8810128",
    preprod: "5d512033-5918-41b1-8f83-b567e8810128",
    production: "5d512033-5918-41b1-8f83-b567e8810128",
  },
  solars: {
    qa: "5d512033-5918-41b1-8f83-b567e8810128",
    preprod: "5d512033-5918-41b1-8f83-b567e8810128",
    production: "5d512033-5918-41b1-8f83-b567e8810128",
  },
  risho: {
    qa: "5b910c96-81b7-49c0-b2a3-f66be295c289",
    preprod: "5b910c96-81b7-49c0-b2a3-f66be295c289",
    production: "5b910c96-81b7-49c0-b2a3-f66be295c289",
  },
  "ashtrom-port": {
    qa: null,
    preprod: null,
    production: null,
  },
  nitzavim: {
    qa: "960763f8-5408-4ccd-96fb-c9f3f2ef687c",
    preprod: "960763f8-5408-4ccd-96fb-c9f3f2ef687c",
    production: "960763f8-5408-4ccd-96fb-c9f3f2ef687c",
  },
}

/**
 * Get the subsidiaryKey for a specific app based on current environment
 *
 * Automatically detects the environment from process.env.ENV:
 * - "production" → uses production keys
 * - "preprod" → uses preprod keys
 * - anything else (including "qa") → uses qa keys
 *
 * @param appName - The app name (folder name under apps/)
 * @returns The subsidiaryKey GUID or array of GUIDs for the app
 *
 * @example
 * ```tsx
 * // Single GUID app
 * const key = getSubsidiaryKey("ashtrom-corporate")
 * // Returns: "9e366047-f965-40b7-8a4c-64e4c6a48951" (based on current ENV)
 *
 * // Array GUID app (engineering)
 * const keys = getSubsidiaryKey("ashtrom-engineering")
 * // Returns: ["c6910c55-...", "f5200088-...", ...]
 * ```
 */
export function getSubsidiaryKey(appName: AppName): SubsidiaryKey | string {
  const config = SUBSIDIARY_KEYS[appName]

  if (!config) {
    console.warn(`[subsidiaryKeyConfig] Unknown app name: ${appName}`)
    return ""
  }

  // Determine environment from process.env.ENV
  const currentEnv = process.env.ENV || ""
  let env: Environment = "qa" // default to qa

  if (currentEnv.includes("production")) {
    env = "production"
  } else if (currentEnv.includes("preprod")) {
    env = "preprod"
  }

  return config[env] || ""
}

/**
 * List of apps that use an array of subsidiaryKeys instead of a single GUID
 */
export const APPS_WITH_ARRAY_KEYS: AppName[] = ["ashtrom-engineering"]

/**
 * Apps that share the same subsidiaryKey across multiple apps
 * This is intentional and documented here for reference
 */
export const SHARED_SUBSIDIARY_KEYS = {
  // Shared by: ashtrom-energy, tierra-bonita, el-patrimonio, solars
  "5d512033-5918-41b1-8f83-b567e8810128": [
    "ashtrom-energy",
    "tierra-bonita",
    "el-patrimonio",
    "solars",
  ],
  // Shared by: ashtrom-properties, risho
  "5b910c96-81b7-49c0-b2a3-f66be295c289": ["ashtrom-properties", "risho"],
  // Shared by: ashtrom-industries, quarries
  "f094ee3d-b676-4644-8e9f-90b12c6ff214": ["ashtrom-industries", "quarries"],
  // Shared by: ashtrom-corporate, bg-bond-rebuild
  "9e366047-f965-40b7-8a4c-64e4c6a48951": ["ashtrom-corporate", "bg-bond-rebuild"],
} as const

/**
 * How to add a new app:
 *
 * 1. Run the fetch script to get GUIDs from Umbraco:
 *    ```bash
 *    npx tsx scripts/fetch-subsidiary-keys.ts
 *    ```
 *
 * 2. Add the app name to the AppName type above
 *
 * 3. Add the configuration to SUBSIDIARY_KEYS object with qa, preprod, and production values
 *
 * 4. If the app uses multiple GUIDs (array), add it to APPS_WITH_ARRAY_KEYS
 *
 * 5. Export this function in packages/utils/index.ts if not already exported
 *
 * 6. Use in the app's constants file:
 *    ```tsx
 *    import { getSubsidiaryKey } from "utils"
 *
 *    export const subsidiaryKey = getSubsidiaryKey("your-app-name")
 *    ```
 */
